<template>
    <div>
        <h4>Audit Documents</h4>
        <b-row class="mb-3">
            <b-col>
                <vue-dropzone id="audit-dropzone" ref="auditDropzone" :options="dropzoneOptions"
                    @vdropzone-success-multiple="dropzoneUploadSuccess" @vdropzone-error-multiple="dropzoneUploadError"
                    @vdropzone-sending="dropzoneSending">
                </vue-dropzone>
            </b-col>
        </b-row>
        <b-row v-if="docuList.length > 0">
            <b-col>
                <table width="100%" id="audit-docs">
                    <tr>
                        <th width="5%">Include</th>
                        <th width="45%">File Name</th>
                        <th width="25%">Uploaded</th>
                        <th width="15%">Uploaded By</th>
                        <th width="10%">Action</th>
                    </tr>
                    <tr v-for="doc in docuList" :key="doc.id">
                        <td class="text-center">
                            <b-form-checkbox :checked="doc.included" @change="toggleExcluded(doc, $event)">
                            </b-form-checkbox>
                        </td>
                        <td @click="viewDocument(doc)" class="cursor-pointer">
                            {{ displayFileName(doc.fileName) }}
                        </td>
                        <td>{{ formattedDate(doc.uploaded) }}</td>
                        <td>{{ doc.uploadedBy }}</td>
                        <td>
                            <b-button size="sm" variant="danger" @click="removeDocument(doc)">
                                <i class="fa fa-trash" aria-hidden="true"></i>
                            </b-button>
                        </td>
                    </tr>
                </table>
            </b-col>
        </b-row>
    </div>
</template>
<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import axios, { AxiosRequestConfig } from 'axios';
import Vue2Dropzone from 'vue2-dropzone';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { AuditDocument } from '@/models/Audit';
import moment from 'moment';

@Component({
    name: "AuditDocumentUpload",
    components: {
        "VueDropzone": Vue2Dropzone,
    }
})
export default class AuditDocumentUpload extends Vue {
    @Prop({ type: Number, required: true }) private auditId!: number;
    private docuList: Array<AuditDocument & { included: boolean }> = [];

    created() {
        this.loadDocuments();
    }

    loadDocuments() {
        this.docuList = [];
        axios.get<Array<AuditDocument>>(`AuditTracking/get-audit-documents/${this.auditId}`)
            .then(res => {
                this.docuList = res.data.map(d => ({ ...d, included: !d.excluded }));
            })
            .catch(err => {
                console.error("Error loading audit documents.", { err, response: err.response });
                this.$notification(NotificationOptions.error(err));
            });
    }

    get dropzoneOptions(): any {
        return {
            url: `${axios.defaults.baseURL}/AuditTracking/upload-audit-documents/${this.auditId}`,
            thumbnailWidth: 150,
            withCredentials: true,
            paramName: 'documents',
            uploadMultiple: true,
            acceptedFiles: 'application/pdf'
        };
    }

    dropzoneUploadSuccess(files: any, response: any) {
        const dropzone: any = this.$refs.auditDropzone;
        (Array.isArray(files) ? files : [files]).forEach((f: any) => dropzone.removeFile(f));
        this.loadDocuments();
    }

    dropzoneUploadError(files: any, message: any) {
        const dropzone: any = this.$refs.auditDropzone;
        (Array.isArray(files) ? files : [files]).forEach((f: any) => dropzone.removeFile(f));
        this.$notification(NotificationOptions.error(message));
    }

    dropzoneSending(file: any, xhr: XMLHttpRequest, _formData: FormData) {
        xhr.setRequestHeader("Authorization", axios.defaults.headers["Authorization"]?.toString() ?? "");
    }

    toggleExcluded(doc: AuditDocument & { included: boolean }, newIncluded: boolean) {
        const excluded = !newIncluded;
        axios.post(`AuditTracking/audit-document/${doc.id}/excluded?excluded=${excluded}`)
            .then(() => {
                doc.included = newIncluded;
            })
            .catch(err => {
                console.error("Error toggling document exclusion.", { err, response: err.response });
                this.$notification(NotificationOptions.error(err));
            });
    }

    removeDocument(doc: AuditDocument & { included: boolean }) {
        this.$bvModal
            .msgBoxConfirm("Are you sure you want to remove this document?", {
                title: "Confirm",
                okVariant: "danger",
                centered: true,
            })
            .then((value) => {
                if (value) {
                    axios.delete(`AuditTracking/audit-document/${doc.id}`)
                        .then(() => {
                            this.loadDocuments();
                            this.$notification(
                                NotificationOptions.notificationOptionsPreset(
                                    "Document removed successfully",
                                    NotificationOptions.NotificationTypes.success
                                )
                            );
                        })
                        .catch(err => {
                            console.error("Error removing document.", { err, response: err.response });
                            this.$notification(NotificationOptions.error(err));
                        });
                }
            })
            .catch(err => {
                console.error(err);
            });
    }

    viewDocument(doc: AuditDocument) {
        const filename = doc.fileName.split("/")[1];
        const config: AxiosRequestConfig = {
            params: { filename: filename, auditId: this.auditId },
            responseType: "blob",
        };
        axios.get<any>("AuditTracking/get-audit-document-file", config)
            .then(res => {
                const objectUrl = URL.createObjectURL(res.data);
                window.open(objectUrl, "_blank");
            })
            .catch(err => {
                console.error("Error viewing document.", { err, response: err.response });
                this.$notification(NotificationOptions.error(err));
            });
    }

    displayFileName(fileName: string): string {
        if (fileName.indexOf('/') > 0)
            return fileName.split('/')[1];
        return fileName;
    }

    formattedDate(value: string): string {
        if (!value) return '';
        return moment.utc(value).local().format("MM/DD/YYYY hh:mm A");
    }
}
</script>
<style scoped>
#audit-docs {
    font-family: Arial, Helvetica, sans-serif;
    border-collapse: collapse;
    width: 100%;
}

#audit-docs td {
    border: 1px solid #ddd;
    padding: 4px 8px;
}

#audit-docs tr:nth-child(even) {
    background-color: #f2f2f2;
}

#audit-docs tr:hover {
    background-color: #ddd;
}

#audit-docs th {
    padding: 8px;
    text-align: left;
    background-color: white;
    color: black;
    border: 1px solid #ddd;
}

.cursor-pointer {
    cursor: pointer;
}
</style>
